import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic, MicOff, IndianRupee, Plus, Sparkles, Check, X,
  ArrowRight, RefreshCw, Volume2, AlertCircle, ShoppingBag,
  Coffee, Fuel, Zap, HelpCircle
} from 'lucide-react';
import { parseNaturalLanguage, createTransaction } from '../../api/expenses';

/**
 * Smart Client-side parser with full support for:
 * 1. Multi-item statements (e.g. "मैं 20 रुपए का सब्जी लाया आलू का और 20 रुपए का टमाटर लाया और आईएस 40 का कड़ी लाया एक किलो")
 * 2. Strict zero-amount safeguard (e.g. saying "हेलो" will NOT cut 50 rs or create an expense)
 */
function parseSpokenTextClient(text) {
  const lower = text.toLowerCase().trim();
  const todayStr = new Date().toISOString().split('T')[0];

  const parseSegment = (seg) => {
    const sLower = seg.toLowerCase().trim();
    let amt = 0;
    const numMatch = sLower.match(/(?:₹|rs\.?|inr)?\s*([0-9]+(?:\.[0-9]{1,2})?)\s*(?:rupay|rupaye|rupee|rupees|rs|ka|ke|pe|रुपए|रुपये|का|की|के)?/i);
    if (numMatch) {
      amt = parseFloat(numMatch[1]);
    } else {
      // Fallback for spoken Hindi number words
      if (/(?:^|\s|[^a-zA-Z0-9])(?:बीस|bees|bis)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 20;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:तीस|tees|tis)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 30;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:चालीस|chalis|chaalis)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 40;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:पचास|pachas|pachaas)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 50;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:साठ|saath|sath)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 60;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:सत्तर|sattar)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 70;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:अस्सी|assi)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 80;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:नब्बे|nabbe)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 90;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:सौ|sau|so)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 100;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:हजार|hazar|hazaar)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 1000;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:दस|das)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 10;
      else if (/(?:^|\s|[^a-zA-Z0-9])(?:पांच|पाँच|panch)(?:$|\s|[^a-zA-Z0-9])/i.test(sLower)) amt = 5;
    }

    let cat = 'Other';
    let subcat = 'General';
    let desc = seg;

    if (/आलू|aalu|potato/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'आलू (Aalu)';
    } else if (/टमाटर|tamatar|tomato/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'टमाटर (Tamatar)';
    } else if (/ककड़ी|कड़ी|कड़ी|ककड़ी|kakdi|kadi|cucumber/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'ककड़ी (Kakdi)';
    } else if (/प्याज|कांदा|pyaz|onion/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'प्याज (Onion)';
    } else if (/भिंडी|bhindi|okra/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'भिंडी (Bhindi)';
    } else if (/गोभी|gobhi|cauliflower|cabbage/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'गोभी (Gobhi)';
    } else if (/मिर्ची|मिर्च|mirchi|chilli/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'मिर्ची (Mirchi)';
    } else if (/धनिया|dhaniya|coriander/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'धनिया (Dhaniya)';
    } else if (/अदरक|adrak|ginger/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'अदरक (Adrak)';
    } else if (/लहसुन|lahsun|garlic/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'लहसुन (Lahsun)';
    } else if (/सब्जी|भाजी|bhaji|vegetable|sabzi/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Vegetables';
      desc = 'सब्जी (Sabzi)';
    } else if (/दूध|milk/i.test(sLower)) {
      cat = 'Food';
      subcat = 'Groceries';
      desc = 'दूध (Milk)';
    } else if (/samosa|chai|चाय|lunch|dinner|breakfast|khana|khaya|food|restaurant|burger|pizza/i.test(sLower)) {
      cat = 'Food';
      subcat = /samosa|chai|चाय|snacks/i.test(sLower) ? 'Snacks' : 'Restaurant';
      desc = /samosa/i.test(sLower) ? 'Samosa' : /chai|चाय/i.test(sLower) ? 'Chai' : 'Food';
    } else if (/petrol|diesel|fuel|rickshaw|riksha|cab|uber|ola|bus|train|पैट्रोल|पेट्रोल/i.test(sLower)) {
      cat = 'Transport';
      subcat = /petrol|diesel|fuel|पैट्रोल|पेट्रोल/i.test(sLower) ? 'Fuel' : 'Cab';
      desc = /petrol|fuel|पैट्रोल|पेट्रोल/i.test(sLower) ? 'Petrol' : 'Transport';
    } else if (/shopping|clothes|clothing|shirt|शर्ट|tshirt|t-shirt|jeans|pant|pants|shoes|sneakers|dress/i.test(sLower)) {
      cat = 'Shopping';
      subcat = 'Clothes';
      desc = /shirt|शर्ट/i.test(sLower) ? 'Shirt' : /jeans/i.test(sLower) ? 'Jeans' : /shoes/i.test(sLower) ? 'Shoes' : 'Shopping';
    } else if (/electricity|bill|light bill|wifi|recharge|mobile|बिजली/i.test(sLower)) {
      cat = 'Bills';
      subcat = 'Mobile';
      desc = 'Utility Bill / Recharge';
    } else {
      let clean = seg.replace(/^(?:मैं|मै|mene|maine|humne|aaj|today)\s*/i, '');
      clean = clean.replace(/(?:आईएस|is)?\s*(?:₹|rs\.?|inr)?\s*[0-9]+(?:\.[0-9]{1,2})?\s*(?:rupay|rupaye|rupee|rupees|rs|ka|ki|ke|रुपए|रुपये|का|की|के)?\s*/gi, '');
      clean = clean.replace(/\s*(?:लाया|लिया|खरीदा|liya|kharida|bought|diya|leke\s+aaya)\s*/gi, '');
      desc = clean.trim() || 'Voice Expense';
    }

    return { amount: amt, category: cat, subcategory: subcat, description: desc };
  };

  // 1. Check for multi-item statements split by "aur", "और", "and", ",", "plus", etc.
  let rawSegments = text.split(/\s*(?:,|\+)\s*|\s+(?:और|aur|and|tatha|phir|fir)\s+/i);

  // If no word connectors found, check if multiple numbers exist and split by number boundaries
  if (rawSegments.length <= 1) {
    const numberMatches = [...text.matchAll(/(?:₹|rs\.?|inr)?\s*([0-9]+(?:\.[0-9]{1,2})?)\s*(?:rupay|rupaye|rupee|rupees|rs|रुपए|रुपये)?/gi)];
    if (numberMatches.length > 1) {
      const segments = [];
      for (let i = 0; i < numberMatches.length; i++) {
        const start = numberMatches[i].index || 0;
        const end = (i + 1 < numberMatches.length) ? (numberMatches[i + 1].index || text.length) : text.length;
        segments.push(text.substring(start, end).trim());
      }
      if (segments.length > 1) {
        rawSegments = segments;
      }
    }
  }

  const multiItems = [];

  for (const rawSeg of rawSegments) {
    if (!rawSeg.trim()) continue;
    const parsedSeg = parseSegment(rawSeg);
    if (parsedSeg.amount > 0) {
      multiItems.push({
        type: 'EXPENSE',
        amount: parsedSeg.amount,
        currency: 'INR',
        category: parsedSeg.category,
        subcategory: parsedSeg.subcategory,
        description: parsedSeg.description,
        transaction_date: todayStr,
        payment_method: /upi|gpay|phonepe|paytm/i.test(lower) ? 'UPI' : 'Cash',
        confidence: 0.95,
      });
    }
  }

  if (multiItems.length > 1) {
    const totalAmount = multiItems.reduce((sum, item) => sum + item.amount, 0);
    return {
      type: 'EXPENSE',
      amount: totalAmount,
      currency: 'INR',
      category: multiItems[0].category,
      subcategory: multiItems[0].subcategory,
      description: multiItems.map((i) => i.description).join(', '),
      transaction_date: todayStr,
      payment_method: /upi|gpay|phonepe|paytm/i.test(lower) ? 'UPI' : 'Cash',
      confidence: 0.95,
      multiple_expenses: multiItems,
    };
  } else if (multiItems.length === 1) {
    return {
      ...multiItems[0],
      multiple_expenses: multiItems,
    };
  }

  // 2. Single item fallback
  const single = parseSegment(text);

  // CRITICAL: If no amount was detected, amount MUST BE 0!
  return {
    type: 'EXPENSE',
    amount: single.amount > 0 ? single.amount : 0,
    currency: 'INR',
    category: single.category,
    subcategory: single.subcategory,
    description: single.description,
    transaction_date: todayStr,
    payment_method: /upi|gpay|phonepe|paytm/i.test(lower) ? 'UPI' : 'Cash',
    confidence: single.amount > 0 ? 0.95 : 0,
    multiple_expenses: single.amount > 0 ? [{
      type: 'EXPENSE',
      amount: single.amount,
      currency: 'INR',
      category: single.category,
      subcategory: single.subcategory,
      description: single.description,
      transaction_date: todayStr,
      payment_method: 'Cash',
      confidence: 0.95,
    }] : [],
  };
}

export default function VoiceExpenseFab({
  onOpenAddExpense,
  onTransactionCreated,
  categories = [],
  accounts = []
}) {
  // ─── ICON ROTATION ANIMATION STATE ───
  const [iconMode, setIconMode] = useState('expense'); // 'expense' | 'mic'
  const [showTooltip, setShowTooltip] = useState(true);
  const [holdProgress, setHoldProgress] = useState(0);

  // ─── VOICE MODAL STATE ───
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [recognizedExpense, setRecognizedExpense] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successSaved, setSuccessSaved] = useState(false);

  // ─── HOLD TIMING REFS ───
  const holdTimerRef = useRef(null);
  const holdProgressIntervalRef = useRef(null);
  const holdStartTimeRef = useRef(0);
  const isLongPressTriggeredRef = useRef(false);
  const recognitionRef = useRef(null);

  // ─── ALTERNATING ICON ANIMATION ───
  useEffect(() => {
    const interval = setInterval(() => {
      setIconMode((prev) => (prev === 'expense' ? 'mic' : 'expense'));
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  // ─── PERIODIC TOOLTIP BOUNCE ───
  useEffect(() => {
    // Show initially for 6 seconds, then hide
    const initialTimer = setTimeout(() => {
      setShowTooltip(false);
    }, 6500);

    // Periodically re-show for 4 seconds every 22 seconds
    const interval = setInterval(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 4500);
    }, 22000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);

  // ─── SPEECH RECOGNITION SETUP ───
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // ignore
      }
      setIsListening(false);
    }
  }, []);

  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setErrorMessage('Speech Recognition is not natively supported in this browser. You can type your voice command below!');
      return;
    }

    try {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (e) {}
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      // Preferred language for Hinglish / Hindi / Indian English
      recognition.lang = 'hi-IN';

      recognition.onstart = () => {
        setIsListening(true);
        setErrorMessage(null);
        setSuccessSaved(false);
        // Haptic feedback for start
        if (navigator.vibrate) navigator.vibrate(40);
      };

      recognition.onresult = (event) => {
        let currentInterim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            final += event.results[i][0].transcript;
          } else {
            currentInterim += event.results[i][0].transcript;
          }
        }

        if (currentInterim) {
          setInterimText(currentInterim);
        }
        if (final) {
          setTranscript(final);
          setInterimText('');
        }
      };

      recognition.onerror = (event) => {
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setErrorMessage('Microphone access was denied. Please allow microphone permission in browser settings.');
        } else if (event.error === 'no-speech') {
          setErrorMessage('No speech detected. Please speak clearly or try again.');
        } else {
          setErrorMessage(`Speech recognition error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      setErrorMessage(`Microphone initialization error: ${err.message}`);
      setIsListening(false);
    }
  }, []);

  // ─── PROCESS SPOKEN EXPENSE ───
  const processSpokenExpense = useCallback(async (textToProcess) => {
    const query = (textToProcess || transcript || interimText).trim();
    if (!query) {
      setErrorMessage('Please say or type an expense statement (e.g. "mene 500rs ka shirt liya")');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    let extractedData = null;

    try {
      // 1. Try server-side AI / Deterministic NLP parser
      const res = await parseNaturalLanguage(query);
      if (res.data && res.data.extracted && res.data.extracted.amount > 0) {
        extractedData = res.data.extracted;
      }
    } catch (err) {
      // Fallback to client-side smart parser if API fails or offline
      console.warn('Backend NLP parsing failed or offline, using client parser fallback:', err);
    }

    if (!extractedData || !extractedData.amount) {
      extractedData = parseSpokenTextClient(query);
    }

    if (!extractedData || extractedData.amount <= 0) {
      setIsProcessing(false);
      setErrorMessage(`Koi price ya amount detect nahi hua (jaise aapne "${query}" bola). Kripya amount ke saath bole, jaise: "20 rupay aalu" ya "500 ka petrol".`);
      return;
    }

    setRecognizedExpense(extractedData);

    // 2. Automatically save the transaction(s) to user's expenses
    try {
      const defaultAccount = accounts.find((a) => a.is_default) || accounts[0];
      const today = new Date().toISOString().split('T')[0];

      // MULTI-EXPENSE SPLIT:
      // If multiple expenses were spoken (e.g. 20 aalu, 20 tamatar, 40 kakdi),
      // create separate distinct debit transactions for each item!
      const itemsToCreate = (extractedData.multiple_expenses && extractedData.multiple_expenses.length > 0)
        ? extractedData.multiple_expenses
        : [extractedData];

      const createdItems = [];

      for (const item of itemsToCreate) {
        let matchedCategoryId = null;
        if (item.category) {
          const found = categories.find(
            (c) => c.name.toLowerCase() === item.category.toLowerCase()
          );
          if (found) matchedCategoryId = found.id;
        }
        if (!matchedCategoryId && categories.length > 0) {
          // Look for food/vegetable or first expense category
          matchedCategoryId = categories.find((c) => /food|grocer|vegetable/i.test(c.name))?.id ||
                              categories.find((c) => c.type === 'EXPENSE' || c.type === 'BOTH')?.id ||
                              categories[0]?.id;
        }

        const txPayload = {
          type: item.type || 'EXPENSE',
          amount: Number(item.amount),
          currency: item.currency || 'INR',
          category_id: matchedCategoryId || null,
          account_id: defaultAccount?.id || null,
          merchant: item.merchant || null,
          description: item.description || 'Voice Expense',
          transaction_date: item.transaction_date || today,
          payment_method: item.payment_method || 'UPI',
          notes: `Added via Voice Assistant: "${query}"`,
          source: 'VOICE_AI',
        };

        const res = await createTransaction(txPayload);
        const catName = categories.find((c) => c.id === matchedCategoryId)?.name || item.category || 'Food';
        createdItems.push({
          ...item,
          id: res?.data?.id,
          categoryName: catName,
        });
      }

      // Haptic celebration
      if (navigator.vibrate) navigator.vibrate([40, 60, 100]);

      setRecognizedExpense({
        ...extractedData,
        createdItems,
      });
      setSuccessSaved(true);

      if (onTransactionCreated) {
        onTransactionCreated();
      }

      // Auto-close after celebration (allow user 4s to review multi-item breakdown)
      const autoCloseDelay = itemsToCreate.length > 1 ? 4200 : 2800;
      setTimeout(() => {
        setVoiceModalOpen(false);
        setSuccessSaved(false);
        setTranscript('');
        setInterimText('');
        setRecognizedExpense(null);
      }, autoCloseDelay);
    } catch (saveErr) {
      console.error('Failed to auto-save voice expense:', saveErr);
      setErrorMessage(saveErr.response?.data?.message || 'Failed to save transaction to database.');
    } finally {
      setIsProcessing(false);
    }
  }, [transcript, interimText, categories, accounts, onTransactionCreated]);

  // When transcript updates and user is not listening anymore, process automatically if triggered from hold
  useEffect(() => {
    if (!isListening && transcript && voiceModalOpen && !successSaved && !isProcessing && !recognizedExpense) {
      processSpokenExpense(transcript);
    }
  }, [isListening, transcript, voiceModalOpen, successSaved, isProcessing, recognizedExpense, processSpokenExpense]);

  // ─── HOLD / TAP LOGIC (TOUCH & MOUSE - ANDROID & IOS COMPLIANT) ───
  const startHold = (e) => {
    // Prevent default context menu on iOS/Android
    if (e.cancelable && e.type === 'touchstart') {
      // Don't preventDefault here as it blocks click, but prevent contextmenu
    }

    holdStartTimeRef.current = Date.now();
    isLongPressTriggeredRef.current = false;
    setHoldProgress(0);

    // Visual progress fill animation (400ms duration)
    const startTime = Date.now();
    const duration = 380; // ms to activate voice

    if (holdProgressIntervalRef.current) clearInterval(holdProgressIntervalRef.current);
    holdProgressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, Math.round((elapsed / duration) * 100));
      setHoldProgress(pct);
      if (pct >= 100) {
        clearInterval(holdProgressIntervalRef.current);
      }
    }, 25);

    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      // HOLD TRIGGERED!
      isLongPressTriggeredRef.current = true;
      setHoldProgress(100);

      // Haptic feedback
      if (navigator.vibrate) navigator.vibrate([60, 40, 60]);

      // Open Voice Modal and Start Listening
      setVoiceModalOpen(true);
      setTranscript('');
      setInterimText('');
      setErrorMessage(null);
      setSuccessSaved(false);
      setRecognizedExpense(null);
      startListening();
    }, duration);
  };

  const cancelOrFinishHold = (e) => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdProgressIntervalRef.current) {
      clearInterval(holdProgressIntervalRef.current);
      holdProgressIntervalRef.current = null;
    }
    setHoldProgress(0);

    const elapsed = Date.now() - holdStartTimeRef.current;

    if (!isLongPressTriggeredRef.current && elapsed < 380) {
      // Single Tap! Open manual Add Expense form
      if (navigator.vibrate) navigator.vibrate(30);
      if (onOpenAddExpense) {
        onOpenAddExpense();
      }
    } else if (isLongPressTriggeredRef.current) {
      // User released after holding. If they were speaking, stop listening to trigger parsing
      setTimeout(() => {
        stopListening();
      }, 400);
    }
  };

  // Keyboard accessibility
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenAddExpense?.();
    }
  };

  return (
    <>
      {/* ─── FLOATING ACTION BUTTON (BOTTOM RIGHT) ─── */}
      <div
        className="v-fab-container"
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Animated Tooltip / Pill Badge */}
        {showTooltip && !voiceModalOpen && (
          <div
            className="v-fab-tooltip animate-pop-in"
            onClick={(e) => {
              e.stopPropagation();
              onOpenAddExpense?.();
            }}
          >
            <span className="v-fab-tooltip-icon">✨</span>
            <div className="v-fab-tooltip-text">
              <strong>Tap:</strong> Write Expense &bull; <strong>Hold:</strong> Voice AI
            </div>
            <button
              type="button"
              className="v-fab-tooltip-close"
              onClick={(e) => {
                e.stopPropagation();
                setShowTooltip(false);
              }}
              aria-label="Close hint"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* The Animated Floating Button */}
        <button
          type="button"
          className={`v-fab-button ${holdProgress > 0 ? 'v-fab-holding' : ''} ${iconMode === 'mic' ? 'v-fab-mic-mode' : 'v-fab-expense-mode'}`}
          style={{
            borderRadius: '50%',
            WebkitBorderRadius: '50%',
            width: '58px',
            height: '58px',
            minWidth: '58px',
            minHeight: '58px',
            maxWidth: '58px',
            maxHeight: '58px',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            padding: 0
          }}
          onMouseDown={startHold}
          onMouseUp={cancelOrFinishHold}
          onMouseLeave={cancelOrFinishHold}
          onTouchStart={startHold}
          onTouchEnd={cancelOrFinishHold}
          onTouchCancel={cancelOrFinishHold}
          onKeyDown={handleKeyDown}
          title="Tap to Add Expense | Hold for Voice Assistant"
          aria-label="Tap to Add Expense, Hold to activate Voice Expense"
        >
          {/* Glowing Aura Rings */}
          <div className="v-fab-aura-ring v-fab-aura-1" />
          <div className="v-fab-aura-ring v-fab-aura-2" />

          {/* SVG Circular Hold Progress Ring */}
          <svg className="v-fab-progress-svg" viewBox="0 0 68 68">
            <circle
              className="v-fab-progress-bg"
              cx="34"
              cy="34"
              r="30"
              strokeWidth="3.5"
            />
            <circle
              className="v-fab-progress-fill"
              cx="34"
              cy="34"
              r="30"
              strokeWidth="3.5"
              style={{
                strokeDashoffset: `${188.4 - (188.4 * holdProgress) / 100}`,
              }}
            />
          </svg>

          {/* Morphing / Alternating Animated Icons */}
          <div className="v-fab-icon-wrapper">
            <div className={`v-fab-icon-flip ${iconMode === 'mic' ? 'flipped' : ''}`}>
              {/* Front: Expense Icon (Indian Rupee / Receipt with Plus) */}
              <div className="v-fab-icon-face v-fab-icon-front">
                <IndianRupee size={24} strokeWidth={2.4} />
                <span className="v-fab-mini-badge">+</span>
              </div>
              {/* Back: Voice Icon (Microphone with Audio Waves) */}
              <div className="v-fab-icon-face v-fab-icon-back">
                <Mic size={24} strokeWidth={2.4} />
                <span className="v-fab-mini-pulse" />
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* ─── VOICE ASSISTANT MODAL / SHEET ─── */}
      {voiceModalOpen && (
        <div className="v-voice-overlay animate-fade-in" onClick={() => setVoiceModalOpen(false)}>
          <div
            className="v-voice-sheet animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="v-voice-header">
              <div className="v-voice-title-row">
                <div className="v-voice-badge">
                  <Sparkles size={14} className="v-spin-slow" />
                  <span>AI Voice Expense</span>
                </div>
                <div className="v-voice-lang-badge">
                  🇮🇳 Hinglish / Hindi / English
                </div>
              </div>
              <button
                type="button"
                className="v-voice-close-btn"
                onClick={() => {
                  stopListening();
                  setVoiceModalOpen(false);
                }}
                aria-label="Close voice modal"
              >
                <X size={18} />
              </button>
            </div>

            {/* Main Visualizer Area */}
            <div className="v-voice-body">
              {/* Success Celebration Card */}
              {successSaved && recognizedExpense ? (
                <div className="v-voice-success-card animate-pop-in">
                  <div className="v-success-icon-wrap">
                    <Check size={32} strokeWidth={3} />
                  </div>
                  {recognizedExpense.createdItems && recognizedExpense.createdItems.length > 1 ? (
                    <>
                      <h3 className="v-success-title">
                        🎉 {recognizedExpense.createdItems.length} Separate Expenses Added!
                      </h3>
                      <div className="v-success-multi-list">
                        {recognizedExpense.createdItems.map((item, idx) => (
                          <div key={idx} className="v-success-multi-item">
                            <div className="v-multi-item-left">
                              <span className="v-multi-item-dot" />
                              <div className="v-multi-item-texts">
                                <span className="v-multi-item-desc">{item.description}</span>
                                <span className="v-multi-item-cat">{item.categoryName || item.category}</span>
                              </div>
                            </div>
                            <span className="v-multi-item-amt">-₹{item.amount}</span>
                          </div>
                        ))}
                      </div>
                      <div className="v-success-total-row">
                        <span>Total Debited:</span>
                        <strong className="v-success-total-val">-₹{recognizedExpense.amount}</strong>
                      </div>
                      <p className="v-success-note">
                        Each item has been saved as a distinct transaction in your ledger.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="v-success-title">Expense Added Successfully!</h3>
                      <div className="v-success-amount">-₹{recognizedExpense.amount}</div>
                      <div className="v-success-details">
                        <span className="v-pill v-pill-category">
                          <ShoppingBag size={13} /> {recognizedExpense.category}
                        </span>
                        <span className="v-pill v-pill-desc">
                          {recognizedExpense.description}
                        </span>
                      </div>
                      <p className="v-success-note">Saved directly to your expense records.</p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {/* Glowing Soundwave Orb */}
                  <div className={`v-soundwave-orb ${isListening ? 'active-listening' : ''}`}>
                    <div className="v-wave-ring v-wave-1" />
                    <div className="v-wave-ring v-wave-2" />
                    <div className="v-wave-ring v-wave-3" />
                    <button
                      type="button"
                      className={`v-mic-toggle-btn ${isListening ? 'listening' : ''}`}
                      onClick={() => (isListening ? stopListening() : startListening())}
                      title={isListening ? 'Tap to Pause' : 'Tap to Speak'}
                    >
                      {isListening ? (
                        <Mic size={34} className="v-mic-pulse" />
                      ) : (
                        <MicOff size={30} />
                      )}
                    </button>
                  </div>

                  {/* Equalizer Frequency Bars */}
                  {isListening && (
                    <div className="v-audio-equalizer">
                      <span className="v-bar v-bar-1" />
                      <span className="v-bar v-bar-2" />
                      <span className="v-bar v-bar-3" />
                      <span className="v-bar v-bar-4" />
                      <span className="v-bar v-bar-5" />
                      <span className="v-bar v-bar-6" />
                      <span className="v-bar v-bar-7" />
                      <span className="v-bar v-bar-8" />
                    </div>
                  )}

                  {/* Status Indicator */}
                  <div className="v-voice-status">
                    {isProcessing ? (
                      <span className="v-status-processing">
                        <RefreshCw size={15} className="et-spin" /> Analyzing & adding to expenses...
                      </span>
                    ) : isListening ? (
                      <span className="v-status-listening">
                        <span className="v-red-dot" /> Listening... Speak naturally
                      </span>
                    ) : (
                      <span className="v-status-idle">
                        Tap microphone or speak your expense
                      </span>
                    )}
                  </div>

                  {/* Speech Transcript Display Box */}
                  <div className="v-transcript-box">
                    {transcript || interimText ? (
                      <p className="v-transcript-text">
                        "{transcript} {interimText}"
                      </p>
                    ) : (
                      <p className="v-transcript-placeholder">
                        Say something like: <em>"mene 500rs ka shirt liya"</em> or <em>"200 ka petrol"</em>
                      </p>
                    )}
                  </div>

                  {/* Error Alert */}
                  {errorMessage && (
                    <div className="v-voice-error animate-fade-in">
                      <AlertCircle size={15} />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  {/* Quick Example Voice Chips */}
                  <div className="v-example-chips">
                    <span className="v-chips-label">Try saying:</span>
                    {[
                      'mene 500rs ka shirt liya',
                      '200 ka petrol',
                      '30 rupay chai samosa',
                      '1200 d-mart grocery',
                    ].map((example, i) => (
                      <button
                        key={i}
                        type="button"
                        className="v-example-chip"
                        onClick={() => {
                          setTranscript(example);
                          processSpokenExpense(example);
                        }}
                      >
                        "{example}"
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="v-voice-actions">
                    <button
                      type="button"
                      className="v-action-btn v-btn-manual"
                      onClick={() => {
                        stopListening();
                        setVoiceModalOpen(false);
                        onOpenAddExpense?.();
                      }}
                    >
                      <span>Type Manually</span>
                    </button>
                    <button
                      type="button"
                      className="v-action-btn v-btn-submit"
                      disabled={isProcessing || (!transcript && !interimText)}
                      onClick={() => processSpokenExpense(transcript || interimText)}
                    >
                      {isProcessing ? (
                        <RefreshCw size={16} className="et-spin" />
                      ) : (
                        <ArrowRight size={16} />
                      )}
                      <span>{isProcessing ? 'Adding...' : 'Add Expense'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
